import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/components/ui/tables/Table';
import type { TableColumn } from '@/components/ui/tables/Table';
import { colors } from '@/assets/styles/colors';
import { MonitoringColumnHeader } from '@/components/features/attendance/monitoring/MonitoringColumnHeader';
import type { LC_MonitoringFiltersIn, MonitoringPresent } from '@/api/domains/attendance/monitoring';
import {
  useMonitoringPresent,
  MONITORING_PRESENT_DEFAULT_SORT,
} from '@/hooks/attendance/monitoring/useMonitoringPresent';
import { MONITORING_TABLE_MIN_WIDTH } from '@/components/features/attendance/monitoring/monitoringTable.constants';


export interface MonitoringPresentTableProps {
  /** Filtros dimensionales aplicados, emitidos por el panel de filtros compartido del monitoreo. */
  appliedFilters: LC_MonitoringFiltersIn | null;
  /** Token de refresco de la pantalla; cada cambio reconsulta. */
  refreshToken?: number;
}

// Tabla "Presentes" del Monitoreo de Asistencia. Búsqueda, orden y paginación son server-side.
export const MonitoringPresentTable: React.FC<MonitoringPresentTableProps> = ({
  appliedFilters,
  refreshToken,
}) => {
  const { t: translate } = useTranslation();

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    hasLoadedOnce,
    onSearch,
    onSortChange,
  } = useMonitoringPresent(appliedFilters, refreshToken);

  const isInitialLoading = !hasLoadedOnce;
  const isRefreshing = loading && hasLoadedOnce;

  const columns = useMemo<TableColumn<MonitoringPresent>[]>(
    () => [
      { key: 'rut', header: translate('attendance:monitoring.present.columns.rut'), sortable: true },
      { key: 'fullName', header: translate('attendance:monitoring.present.columns.collaborator'), sortable: true },
      // El backend devuelve el código "present"; la etiqueta la resuelve i18n.
      {
        key: 'statusCode',
        header: translate('attendance:monitoring.present.columns.status'),
        sortable: false,
        render: (row) => translate(`attendance:monitoring.present.status.${row.statusCode}`),
      },
    ],
    [translate],
  );

  return (
    <div className="flex flex-col gap-3" data-testid="monitoring-present">
      <MonitoringColumnHeader
        icon="userCheck"
        iconColor={colors.feedback.success300}
        title={translate('attendance:monitoring.present.title')}
        isRefreshing={isRefreshing}
        refreshingTestId="monitoring-present-refreshing"
      />
      <div
        className={`flex h-full flex-1 transition-opacity duration-200 ${isRefreshing ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={isRefreshing}
      >
        <Table<MonitoringPresent>
          className="w-full flex-1"
          minTableWidth={MONITORING_TABLE_MIN_WIDTH}
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isInitialLoading}
          searchable
          searchPlaceholder={translate('attendance:monitoring.present.searchPlaceholder')}
          onSearch={onSearch}
          serverSideSort
          initialSort={MONITORING_PRESENT_DEFAULT_SORT}
          onSortChange={onSortChange}
          getRowId={(row) => row.rut}
          hideTotalRows={total === 0}
          pagination={
            total > 0
              ? { page, pageSize, total, onChange: setPage, onPageSizeChange: setPageSize }
              : undefined
          }
          data-testid="monitoring-present-table"
        />
      </div>
    </div>
  );
};

export default MonitoringPresentTable;
